// Pond wildlife: small, hand-shaped silhouettes with quiet, unhurried motion.
// Static markings are drawn once; movement and limb poses use child transforms where possible.
import { Container, Graphics } from 'pixi.js';
import { sound } from './sound';
import { boundedDelta, keepInWater, rand, turnToward, waterPoint, waterRadius, type WaterHabitat, type WaterPoint } from './habitat';

type RippleFn = (x: number, y: number, strength: number) => void;
interface Anchor { x: number; y: number; r: number }
type Habitat = () => WaterHabitat;
const ease = (rate: number, dt: number) => 1 - Math.exp(-rate * dt);

abstract class Creature {
  readonly g = new Graphics();
  reducedMotion = false;
  abstract tick(dt: number, t: number, ripple: RippleFn): void;
}

/** Fish turn inside an ellipse, their translucent silhouettes staying below the pads. */
class Fish extends Creature {
  private p: WaterPoint;
  private a = rand(-Math.PI, Math.PI);
  private speed = rand(22, 45);
  private turn = 0;
  private changeIn = rand(1, 4);
  private nibble = rand(6, 18);
  private phase = rand(0, Math.PI * 2);
  private tail = new Graphics();

  constructor(private habitat: Habitat) {
    super();
    this.p = waterPoint(habitat(), 65);
    const len = rand(18, 31);
    this.g.alpha = 0.25;
    this.g.ellipse(0, 0, len, len * 0.28).fill(0x254d45);
    this.tail.position.set(-len * 0.72, 0);
    this.tail.poly([0, 0, -len * 0.55, -len * 0.29, -len * 0.55, len * 0.29]).fill(0x254d45);
    this.g.addChild(this.tail);
  }

  tick(dt: number, t: number, ripple: RippleFn): void {
    const h = this.habitat();
    this.changeIn -= dt;
    if (this.changeIn <= 0) { this.turn = rand(-0.25, 0.25); this.changeIn = rand(1.5, 4); }
    const look = { x: this.p.x + Math.cos(this.a) * 90, y: this.p.y + Math.sin(this.a) * 90 };
    if (waterRadius(look, h, 65) > 0.95) this.a = turnToward(this.a, Math.atan2(h.cy - this.p.y, h.cx - this.p.x), dt * 1.4);
    else this.a += this.turn * dt;
    this.p.x += Math.cos(this.a) * this.speed * dt;
    this.p.y += Math.sin(this.a) * this.speed * dt;
    keepInWater(this.p, h, 55);
    this.nibble -= dt;
    if (this.nibble <= 0) {
      ripple(this.p.x, this.p.y, 0.38);
      this.nibble = rand(12, 28);
      if (Math.random() < 0.18) sound.drip();
    }
    this.g.position.set(this.p.x, this.p.y);
    this.g.rotation = this.a;
    this.tail.rotation = Math.sin(t * 5 + this.phase) * 0.22;
  }
}

/** A mossy little shell with four independently paddling feet. */
class Turtle extends Creature {
  private p: WaterPoint;
  private a = rand(-Math.PI, Math.PI);
  private speed = rand(7, 12);
  private rest = rand(0, 3);
  private restIn = rand(12, 24);
  private phase = rand(0, Math.PI * 2);
  private feet: Graphics[] = [];

  constructor(private habitat: Habitat) {
    super();
    this.p = waterPoint(habitat(), 65);
    this.g.ellipse(2, 5, 24, 16).fill({ color: 0x244c39, alpha: 0.14 });
    for (const [x, y] of [[13, -11], [13, 11], [-13, -11], [-13, 11]]) {
      const foot = new Graphics().ellipse(0, 0, 7, 4).fill(0x788655);
      foot.position.set(x, y);
      this.feet.push(foot);
      this.g.addChild(foot);
    }
    const shell = new Graphics();
    shell.ellipse(22, 0, 8, 6).fill(0x8c9a61);
    shell.circle(26, -2, 1.1).fill(0x293a2d);
    shell.ellipse(0, 0, 21, 15).fill(0x566e43).stroke({ color: 0x3d5438, width: 1.5 });
    shell.ellipse(-3, -3, 15, 10).fill(0x75844d);
    shell.poly([-10, 0, -5, -7, 6, -7, 12, 0, 6, 7, -5, 7]).stroke({ color: 0x455e3b, width: 1.2 });
    shell.moveTo(-19, 0).lineTo(-10, 0).moveTo(12, 0).lineTo(21, 0).moveTo(-5, -7).lineTo(-8, -13).moveTo(6, -7).lineTo(10, -12).moveTo(-5, 7).lineTo(-8, 13).moveTo(6, 7).lineTo(10, 12).stroke({ color: 0x455e3b, width: 1 });
    this.g.addChild(shell);
  }

  tick(dt: number, t: number, ripple: RippleFn): void {
    const h = this.habitat();
    if (this.rest > 0) this.rest = Math.max(0, this.rest - dt);
    else {
      this.restIn -= dt;
      const look = { x: this.p.x + Math.cos(this.a) * 65, y: this.p.y + Math.sin(this.a) * 65 };
      if (waterRadius(look, h, 60) > 0.95) this.a = turnToward(this.a, Math.atan2(h.cy - this.p.y, h.cx - this.p.x), dt * 0.8);
      else this.a += Math.sin(t * 0.16 + this.phase) * dt * 0.08;
      this.p.x += Math.cos(this.a) * this.speed * dt;
      this.p.y += Math.sin(this.a) * this.speed * dt;
      if (this.restIn <= 0) { this.rest = rand(3, 7); this.restIn = rand(15, 30); ripple(this.p.x, this.p.y, 0.35); }
    }
    keepInWater(this.p, h, 45);
    this.g.position.set(this.p.x, this.p.y);
    this.g.rotation = this.a;
    const paddle = this.rest > 0 || this.reducedMotion ? 0 : Math.sin(t * 2.8 + this.phase) * 0.28;
    this.feet.forEach((foot, i) => { foot.rotation = (i % 2 ? 1 : -1) * paddle; });
  }
}

/** Anticipation, a small arcing leap, and a soft landing. Perched frogs move with their pad. */
class Frog extends Creature {
  private pad: number;
  private p = { x: 0, y: 0 };
  private from = { x: 0, y: 0 };
  private to = { x: 0, y: 0 };
  private offset = { x: 0, y: 0 };
  private hop = -1;
  private anticipation = 0;
  private landing = 0;
  private wait = rand(5, 12);
  private facing = rand(-Math.PI, Math.PI);
  private blink = 0;
  private blinkIn = rand(2, 5);
  private initialized = false;

  constructor(private anchors: () => Anchor[], private color: number, index: number) { super(); this.pad = index; }

  tick(dt: number, _t: number, ripple: RippleFn): void {
    const anchors = this.anchors();
    if (!anchors.length) { this.g.visible = false; this.initialized = false; return; }
    this.g.visible = true;
    if (this.pad >= anchors.length) { this.pad %= anchors.length; this.initialized = false; this.hop = -1; this.anticipation = 0; }
    const anchor = anchors[this.pad];
    if (!this.initialized) {
      this.offset = { x: rand(-anchor.r * 0.32, anchor.r * 0.32), y: rand(-anchor.r * 0.23, anchor.r * 0.23) };
      this.initialized = true;
    }
    if (this.reducedMotion) { this.hop = -1; this.anticipation = 0; this.landing = 0; }
    if (this.hop < 0 && this.anticipation <= 0) {
      this.p.x = anchor.x + this.offset.x;
      this.p.y = anchor.y + this.offset.y;
      this.wait -= dt;
      if (this.wait <= 0) {
        const next = anchors.length > 1 ? (this.pad + 1 + Math.floor(Math.random() * (anchors.length - 1))) % anchors.length : this.pad;
        const target = anchors[next];
        this.from = { ...this.p };
        this.offset = { x: rand(-target.r * 0.32, target.r * 0.32), y: rand(-target.r * 0.23, target.r * 0.23) };
        this.to = { x: target.x + this.offset.x, y: target.y + this.offset.y };
        this.facing = Math.atan2(this.to.y - this.from.y, this.to.x - this.from.x);
        this.pad = next;
        this.anticipation = 0.24;
        this.wait = rand(8, 19);
      }
    } else if (this.anticipation > 0) {
      this.anticipation = Math.max(0, this.anticipation - dt);
      if (this.anticipation === 0) { this.hop = 0; if (Math.random() < 0.45) sound.croak(); }
    } else {
      const target = anchors[this.pad];
      this.to = { x: target.x + this.offset.x, y: target.y + this.offset.y };
      const duration = Math.max(0.5, Math.min(1.8, Math.hypot(this.to.x - this.from.x, this.to.y - this.from.y) / 430));
      this.hop = Math.min(1, this.hop + dt / duration);
      this.p.x = this.from.x + (this.to.x - this.from.x) * this.hop;
      this.p.y = this.from.y + (this.to.y - this.from.y) * this.hop;
      if (this.hop >= 1) { this.hop = -1; this.landing = 0.25; ripple(this.p.x, this.p.y + 9, 0.55); }
    }
    this.landing = Math.max(0, this.landing - dt);
    this.blinkIn -= dt;
    this.blink = Math.max(0, this.blink - dt);
    if (this.blinkIn <= 0) { this.blink = 0.13; this.blinkIn = rand(2.5, 6); }
    const air = this.hop >= 0 ? Math.sin(this.hop * Math.PI) : 0;
    const squash = this.anticipation > 0 ? Math.sin((1 - this.anticipation / 0.24) * Math.PI * 0.5) : this.landing / 0.25;
    const g = this.g;
    g.clear(); g.position.set(this.p.x, this.p.y); g.rotation = this.facing + Math.PI / 2;
    g.scale.set(0.88 * (1 + air * 0.15 + squash * 0.12), 0.88 * (1 + air * 0.15 - squash * 0.18));
    g.ellipse(0, 10, 14, 7).fill({ color: 0x24462d, alpha: 0.16 * (1 - air * 0.6) });
    const stretch = air * 6;
    g.ellipse(-12, 10 + stretch, 6, 9 + stretch).fill(this.color); g.ellipse(12, 10 + stretch, 6, 9 + stretch).fill(this.color);
    g.ellipse(0, 4, 14, 12).fill(this.color); g.ellipse(0, 7, 8, 6).fill(0xdce5ab);
    g.ellipse(0, -8, 11, 8).fill(this.color);
    g.ellipse(-9, 0, 3, 6).fill(this.color); g.ellipse(9, 0, 3, 6).fill(this.color);
    for (const side of [-1, 1]) {
      g.circle(side * 6, -13, 4.2).fill(this.color);
      if (this.blink > 0) g.moveTo(side * 6 - 3, -13).lineTo(side * 6 + 3, -13).stroke({ color: 0x35472b, width: 1.5 });
      else { g.circle(side * 6, -13, 3).fill(0xf3efce); g.circle(side * 6, -13, 1.6).fill(0x28372b); g.circle(side * 6 + 0.5, -13.7, 0.55).fill(0xffffff); }
    }
    g.moveTo(-4, -5).quadraticCurveTo(0, -2.5, 4, -5).stroke({ color: 0x35472b, width: 1 });
  }
}

/** Purposeful short flights and tiny hovering pauses; no per-frame random jitter. */
class Flyer extends Creature {
  private p: WaterPoint;
  private target: WaterPoint;
  private a = rand(-Math.PI, Math.PI);
  private hover = rand(0, 2);
  private phase = rand(0, Math.PI * 2);
  private wings: Graphics[] = [];

  constructor(private W: number, private H: number, private big: boolean) {
    super();
    this.p = { x: rand(120, W - 120), y: rand(100, H - 100) };
    this.target = this.nextTarget();
    if (big) {
      this.g.ellipse(1, 5, 16, 4).fill({ color: 0x35594b, alpha: 0.1 });
      for (const side of [-1, 1]) {
        const wing = new Graphics();
        wing.ellipse(0, side * 6, 8, 3.3).fill({ color: 0xe5f0d8, alpha: 0.7 });
        wing.ellipse(-6, side * 5, 6.5, 2.8).fill({ color: 0xd4e7c4, alpha: 0.65 });
        this.wings.push(wing); this.g.addChild(wing);
      }
      const body = new Graphics().roundRect(-13, -1.5, 24, 3, 1.5).fill(0x5b9483).circle(11, 0, 3).fill(0x467265);
      body.circle(12, -1.3, 1).fill(0x283f35);
      this.g.addChild(body);
    } else {
      this.g.circle(0, 0, 1.5).fill({ color: 0x5d6552, alpha: 0.7 });
      this.g.ellipse(-1, -2, 2.3, 1).fill({ color: 0xf3edd6, alpha: 0.6 });
    }
  }

  private nextTarget(): WaterPoint {
    return { x: Math.max(90, Math.min(this.W - 90, this.p.x + rand(-220, 220))), y: Math.max(90, Math.min(this.H - 90, this.p.y + rand(-170, 170))) };
  }

  tick(dt: number, t: number): void {
    if (this.hover > 0) this.hover = Math.max(0, this.hover - dt);
    else {
      let distance = Math.hypot(this.target.x - this.p.x, this.target.y - this.p.y);
      if (distance < 5) {
        this.target = this.nextTarget();
        distance = Math.hypot(this.target.x - this.p.x, this.target.y - this.p.y);
        this.hover = this.big ? rand(0.6, 2.4) : rand(0.1, 0.5);
      }
      if (this.hover <= 0 && distance > 0) {
        this.a = turnToward(this.a, Math.atan2(this.target.y - this.p.y, this.target.x - this.p.x), dt * 5);
        // Slow near a perch so a finite turning radius cannot leave the insect orbiting it forever.
        const step = Math.min(distance, (this.big ? 95 : 52) * dt, distance * ease(2.5, dt));
        this.p.x = Math.max(70, Math.min(this.W - 70, this.p.x + Math.cos(this.a) * step));
        this.p.y = Math.max(70, Math.min(this.H - 70, this.p.y + Math.sin(this.a) * step));
      }
    }
    this.g.position.set(this.p.x, this.p.y + (this.reducedMotion ? 0 : Math.sin(t * 3 + this.phase) * 1.4));
    this.g.rotation = this.a;
    const flutter = this.reducedMotion ? 0.8 : 0.7 + Math.sin(t * 38 + this.phase) * 0.28;
    this.wings.forEach((wing) => wing.scale.y = flutter);
  }
}

/** Mother and three ducklings: warm brown feathers, butter-yellow chicks, a gently curving procession. */
class DuckFamily extends Creature {
  private mother: WaterPoint;
  private target: WaterPoint;
  private a = 0;
  private speed = 0;
  private rest = 2;
  private restIn = rand(15, 25);
  private callIn = rand(10, 20);
  private wakeIn = 0;
  private placedWithPads = false;
  private motherSprite = new Graphics();
  private chicks: Array<{ p: WaterPoint; a: number; sprite: Graphics }> = [];

  constructor(private habitat: Habitat, private anchors: () => Anchor[]) {
    super();
    const h = habitat();
    this.mother = { x: h.cx + h.rx * 0.08, y: h.cy + h.ry * 0.45 };
    this.target = waterPoint(h, 100, 0.72);
    this.drawDuck(this.motherSprite, false);
    this.g.addChild(this.motherSprite);
    for (let i = 0; i < 3; i++) {
      const sprite = new Graphics();
      this.drawDuck(sprite, true);
      const p = { x: this.mother.x - 48 - i * 30, y: this.mother.y + Math.sin(i * 1.4) * 3 };
      keepInWater(p, h, 55);
      this.chicks.push({ p, a: 0, sprite }); this.g.addChild(sprite);
    }
  }

  private drawDuck(g: Graphics, chick: boolean): void {
    const size = chick ? 0.55 : 1;
    g.scale.set(size);
    g.ellipse(-1, 6, 24, 10).fill({ color: 0x294e40, alpha: 0.14 });
    g.poly([-16, -3, -29, -9, -24, 2, -15, 6]).fill(chick ? 0xe4b44f : 0x73563e);
    g.ellipse(-2, 0, 22, 14).fill(chick ? 0xf5d878 : 0x9a7953).stroke({ color: chick ? 0xd4a94c : 0x705339, width: 1.2 });
    g.ellipse(-5, -1, 14, 9).fill(chick ? 0xffe5a0 : 0xb99a6d);
    g.ellipse(-7, -4, 10, 5).fill({ color: 0xfff1cf, alpha: chick ? 0.55 : 0.22 });
    if (!chick) {
      g.moveTo(-15, -1).quadraticCurveTo(-7, 7, 5, 2).moveTo(-12, 3).quadraticCurveTo(-4, 9, 7, 4).stroke({ color: 0x826345, width: 1.3 });
      g.ellipse(1, 2, 7, 3).fill(0x6a8179).stroke({ color: 0xdfd2b0, width: 1.3 });
    }
    g.ellipse(15, -7, 10, 10).fill(chick ? 0xffe29a : 0xbca079);
    g.moveTo(7, -8).quadraticCurveTo(15, -5, 23, -7).stroke({ color: chick ? 0xcaa64c : 0x66503a, width: chick ? 1.7 : 2.3 });
    g.poly([23, -7, 33, -5, 32, -1, 23, 0]).fill(chick ? 0xdba04e : 0xc58a4c).stroke({ color: 0xa37444, width: 0.7 });
    g.circle(18, -9, 1.8).fill(0x28372e).circle(18.5, -9.5, 0.6).fill(0xfff9df);
  }

  private chooseTarget(): void {
    for (let attempt = 0; attempt < 12; attempt++) {
      this.target = waterPoint(this.habitat(), 100, 0.82);
      if (this.anchors().every((pad) => Math.hypot(this.target.x - pad.x, this.target.y - pad.y) > pad.r + 60)) return;
    }
  }

  /** Anchors arrive after construction. Start the whole procession in a clear channel, never on a pad. */
  private placeFamily(): void {
    const h = this.habitat();
    let candidate = { x: h.cx + h.rx * 0.08, y: h.cy + h.ry * 0.45 };
    for (let attempt = 0; attempt < 48; attempt++) {
      const points = [candidate, ...this.chicks.map((_, index) => ({ x: candidate.x - 48 - index * 30, y: candidate.y }))];
      const clear = points.every((p) => waterRadius(p, h, 65) <= 0.96 && this.anchors().every((pad) => Math.hypot(p.x - pad.x, p.y - pad.y) > pad.r + 45));
      if (clear) break;
      candidate = waterPoint(h, 100, 0.78);
    }
    this.mother = candidate;
    this.a = 0;
    this.chicks.forEach((chick, index) => { chick.p = { x: candidate.x - 48 - index * 30, y: candidate.y }; chick.a = 0; keepInWater(chick.p, h, 40); });
    this.chooseTarget();
    this.placedWithPads = true;
  }

  private clearPads(p: WaterPoint, room: number): void {
    for (const pad of this.anchors()) {
      const dx = p.x - pad.x, dy = p.y - pad.y, distance = Math.hypot(dx, dy), radius = pad.r + room;
      if (distance < radius) {
        p.x = pad.x + (distance > 0.1 ? dx / distance : 1) * radius;
        p.y = pad.y + (distance > 0.1 ? dy / distance : 0) * radius;
      }
    }
  }

  tick(dt: number, t: number, ripple: RippleFn): void {
    const h = this.habitat();
    if (!this.placedWithPads && this.anchors().length) this.placeFamily();
    if (this.rest > 0) this.rest = Math.max(0, this.rest - dt);
    else {
      this.restIn -= dt;
      if (Math.hypot(this.target.x - this.mother.x, this.target.y - this.mother.y) < 45) this.chooseTarget();
      if (this.restIn <= 0) { this.rest = rand(3, 6); this.restIn = rand(16, 28); }
    }
    let dx = this.target.x - this.mother.x, dy = this.target.y - this.mother.y;
    const distance = Math.max(1, Math.hypot(dx, dy));
    dx /= distance; dy /= distance;
    for (const pad of this.anchors()) {
      const px = this.mother.x - pad.x, py = this.mother.y - pad.y;
      const d = Math.hypot(px, py), edge = pad.r + 90;
      if (d < edge) {
        const nx = d > 0.1 ? px / d : 1, ny = d > 0.1 ? py / d : 0;
        const side = dx * ny - dy * nx >= 0 ? -1 : 1;
        const force = Math.min(2.5, (edge - d) / 90 * 1.5);
        // Include a tangent: a purely outward force can stall head-on against a large event pad.
        dx += (nx - ny * side * 0.85) * force;
        dy += (ny + nx * side * 0.85) * force;
      }
    }
    if (waterRadius(this.mother, h, 100) > 0.92) { dx += (h.cx - this.mother.x) / h.rx * 3; dy += (h.cy - this.mother.y) / h.ry * 3; }
    this.a = turnToward(this.a, Math.atan2(dy, dx), dt * 0.8);
    this.speed += ((this.rest > 0 ? 0 : 31) - this.speed) * ease(1.7, dt);
    this.mother.x += Math.cos(this.a) * this.speed * dt;
    this.mother.y += Math.sin(this.a) * this.speed * dt;
    this.clearPads(this.mother, 33);
    keepInWater(this.mother, h, 65);
    this.motherSprite.position.set(this.mother.x, this.mother.y);
    this.motherSprite.rotation = this.a + (this.reducedMotion ? 0 : Math.sin(t * 2.6) * 0.012);
    let leader = this.mother, heading = this.a;
    for (const [index, chick] of this.chicks.entries()) {
      const gap = index === 0 ? 48 : 30;
      const tx = leader.x - Math.cos(heading) * gap, ty = leader.y - Math.sin(heading) * gap;
      const cx = tx - chick.p.x, cy = ty - chick.p.y, d = Math.hypot(cx, cy);
      const step = Math.min(62 * dt, d * ease(3.5, dt));
      if (d > 0.05) {
        chick.p.x += cx / d * step; chick.p.y += cy / d * step;
      }
      chick.a = turnToward(chick.a, d > 3 ? Math.atan2(cy, cx) : heading, dt * 3);
      this.clearPads(chick.p, 20);
      keepInWater(chick.p, h, 40);
      chick.sprite.position.set(chick.p.x, chick.p.y);
      chick.sprite.rotation = chick.a + (this.reducedMotion ? 0 : Math.sin(t * 3.5 + index) * 0.025);
      leader = chick.p; heading = chick.a;
    }
    this.wakeIn -= dt;
    if (this.speed > 8 && this.wakeIn <= 0) { ripple(this.mother.x - Math.cos(this.a) * 22, this.mother.y - Math.sin(this.a) * 22, 0.24); this.wakeIn = 1.7; }
    this.callIn -= dt;
    if (this.callIn <= 0) { sound.duck(); this.callIn = rand(22, 40); }
  }
}

/** Stable markings, a slow head dip, and occasional ears/tail movement on the left bank. */
class Deer extends Creature {
  private drink = 0;
  private idle = rand(5, 12);
  private earFlick = 0;
  private headDown = 0;
  private spots = Array.from({ length: 13 }, () => ({ x: rand(-24, 20), y: rand(-8, 8), r: rand(1.1, 1.7) }));
  constructor(private habitat: Habitat) { super(); }

  tick(dt: number, t: number, ripple: RippleFn): void {
    const h = this.habitat(), y = h.cy + h.ry * 0.3;
    const x = h.cx - h.rx * Math.sqrt(1 - 0.3 ** 2) - 62;
    if (this.drink > 0) { this.drink = Math.max(0, this.drink - dt); if (Math.random() < dt * 0.45) ripple(x + 77, y + 8, 0.26); }
    else { this.idle -= dt; if (this.idle <= 0) { this.drink = rand(3, 6); this.idle = rand(9, 20); } }
    this.earFlick = Math.max(0, this.earFlick - dt);
    if (Math.random() < dt * 0.12) this.earFlick = 0.3;
    this.headDown += ((this.drink > 0 ? 1 : 0) - this.headDown) * ease(2.5, dt);
    const g = this.g;
    g.clear(); g.position.set(x, y);
    g.ellipse(0, 6, 40, 14).fill({ color: 0x514b35, alpha: 0.17 });
    for (const [lx, ly] of [[-26, -9], [-26, 9], [20, -10], [20, 10]]) g.ellipse(lx, ly, 4, 3).fill(0x63513d);
    g.ellipse(0, 0, 36, 15).fill(0xb08b63); g.ellipse(-8, -2, 23, 11).fill(0xc3a078);
    for (const spot of this.spots) g.circle(spot.x, spot.y, spot.r).fill({ color: 0xf5e4bd, alpha: 0.8 });
    g.ellipse(-38, Math.sin(t * 1.7) * 1.6, 6, 3).fill(0xf1e3c8);
    const hx = 45 + this.headDown * 14, hy = this.headDown * 10;
    g.moveTo(30, 0).lineTo(hx, hy).stroke({ color: 0xb08b63, width: 12, cap: 'round' });
    g.ellipse(hx + 6, hy, 11, 7).fill(0xb08b63); g.circle(hx + 15, hy, 2.5).fill(0x493c2c);
    g.circle(hx + 6, hy - 4, 1.6).fill(0x344033).circle(hx + 6.5, hy - 4.5, 0.5).fill(0xffffff);
    const ear = Math.sin(this.earFlick / 0.3 * Math.PI) * 3;
    for (const side of [-1, 1]) {
      g.ellipse(hx - 2, hy + side * (9 + ear), 4, 7).fill(0xb08b63);
      g.ellipse(hx - 2, hy + side * (9 + ear), 2, 4).fill(0xe5c8aa);
    }
  }
}

export class Creatures {
  private under = new Container();
  private surface = new Container();
  private above = new Container();
  private all: Creature[] = [];
  private anchors: Anchor[] = [];
  private habitat: WaterHabitat;
  private reducedMotion = false;
  private time = 0;

  constructor(world: Container, W: number, H: number, S: number) {
    this.habitat = { cx: W / 2, cy: H / 2, rx: W / 2 - S, ry: H / 2 - S };
    const water = () => this.habitat;
    world.addChild(this.under, this.surface);
    for (let i = 0; i < 6; i++) this.add(new Fish(water), this.under);
    for (let i = 0; i < 2; i++) this.add(new Turtle(water), this.surface);
    this.add(new DuckFamily(water, () => this.anchors), this.above);
    [0x7d994d, 0x92ad60, 0x687f42].forEach((color, index) => this.add(new Frog(() => this.anchors, color, index), this.above));
    for (let i = 0; i < 3; i++) this.add(new Flyer(W, H, true), this.above);
    for (let i = 0; i < 6; i++) this.add(new Flyer(W, H, false), this.above);
    this.add(new Deer(water), this.above);
  }

  addAboveLayer(world: Container): void { world.addChild(this.above); }
  setPadAnchors(anchors: Anchor[]): void { this.anchors = anchors; }

  setHabitat(habitat: WaterHabitat): void {
    if ([habitat.cx, habitat.cy, habitat.rx, habitat.ry].every(Number.isFinite) && habitat.rx > 0 && habitat.ry > 0) this.habitat = { ...habitat };
  }

  /** Freeze ambient travel and large gestures; frogs settle immediately onto their current pads. */
  setReducedMotion(reduced: boolean): void {
    this.reducedMotion = reduced;
    for (const creature of this.all) creature.reducedMotion = reduced;
  }

  private add(creature: Creature, layer: Container): void {
    this.all.push(creature); layer.addChild(creature.g); creature.g.eventMode = 'none';
  }

  tick(dt: number, _t: number, ripple: RippleFn): void {
    const step = this.reducedMotion ? 0 : boundedDelta(dt);
    this.time += step;
    for (const creature of this.all) creature.tick(step, this.time, ripple);
  }
}
